import {
  TickerAnalysis, MarketData, TechnicalData, OptionsData,
  StepScore, RiskFlags, StrategyRecommendation, TradePlan, PositionSizing,
  OptionLeg, VolatilityMetrics, FOMC_DATES_2025_2026, TOTAL_POSSIBLE_POINTS,
  CoilScore, EARNINGS_THRESHOLDS_V4, VIX_SIZE_MULTIPLIERS
} from '../types';
import { computeVolatilityMetrics } from './volatilityEngine';
import { generateStrategies } from './strategyEngine';

// --- HELPERS ---

function calculateSyntheticPrice(
  baseOptionPrice: number, 
  oldUnderlyingPrice: number, 
  liveUnderlyingPrice: number, 
  delta: number, 
  gamma: number = 0
): number {
  const priceDiff = liveUnderlyingPrice - oldUnderlyingPrice;
  // Delta + Gamma adjustment
  const adjustment = (delta * priceDiff) + (0.5 * gamma * Math.pow(priceDiff, 2));
  return Math.max(0.01, baseOptionPrice + adjustment);
}

function checkFomcProximity(): { days: number | null; date: string | null; risk: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Find next upcoming FOMC date (forward-looking only)
  let nextFomcDate: Date | null = null;
  let nextFomcStr: string | null = null;

  for (const dateStr of FOMC_DATES_2025_2026) {
    const fomcDate = new Date(dateStr);
    fomcDate.setHours(0, 0, 0, 0);
    if (fomcDate >= today) {
      nextFomcDate = fomcDate;
      nextFomcStr = dateStr;
      break; // Array is chronological, first future date is next FOMC
    }
  }

  if (!nextFomcDate) return { days: null, date: null, risk: 'NONE' };

  const diffDays = Math.ceil((nextFomcDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays > 45) return { days: diffDays, date: nextFomcStr, risk: 'NONE' };

  let risk: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' = 'NONE';
  if (diffDays <= 2) risk = 'HIGH';
  else if (diffDays <= 7) risk = 'MEDIUM';
  else if (diffDays <= 14) risk = 'LOW';

  return { days: diffDays, date: nextFomcStr, risk };
}

// --- v4.0: COIL SCORE (VOLATILITY COMPRESSION) ---

function calculateCoilScore(tech: TechnicalData): CoilScore {
  let total = 0;
  let bbWidthSignal = 0;
  let ribbonSignal = 0;
  let kcSqueezeSignal = 0;
  let crossoverSignal = 0;

  // Bollinger Band Width compression (0-3 pts)
  if (tech.bbWidth !== undefined && tech.bbWidth10Pct !== undefined) {
    if (tech.bbWidth <= tech.bbWidth10Pct) {
      bbWidthSignal = 3;
    } else if (tech.bbWidth <= tech.bbWidth10Pct * 1.5) {
      bbWidthSignal = 2;
    } else if (tech.bbWidth <= tech.bbWidth10Pct * 2) {
      bbWidthSignal = 1;
    }
  }

  // MA Ribbon compression (0-3 pts)
  if (tech.ribbonStdDev !== undefined && tech.ribbonStdDev5Pct !== undefined) {
    if (tech.ribbonStdDev <= tech.ribbonStdDev5Pct) {
      ribbonSignal = 3;
    } else if (tech.ribbonStdDev <= tech.ribbonStdDev5Pct * 1.5) {
      ribbonSignal = 2;
    } else if (tech.ribbonStdDev <= tech.ribbonStdDev5Pct * 2) {
      ribbonSignal = 1;
    }
  }

  // Keltner Channel squeeze (0-2 pts)
  if (tech.isSqueezing) {
    kcSqueezeSignal = 2;
  } else if (tech.squeeze) {
    // Legacy squeeze field as fallback
    kcSqueezeSignal = 1;
  }

  // Post-squeeze EMA crossover (0-2 pts)
  if (tech.squeezeReleased && tech.macd.crossover === 'BULLISH') {
    crossoverSignal = 2;
  } else if (tech.squeezeReleased) {
    crossoverSignal = 1;
  }

  total = bbWidthSignal + ribbonSignal + kcSqueezeSignal + crossoverSignal;

  // Interpret the score
  let interpretation: CoilScore['interpretation'];
  let breakoutProbability: number;

  if (total >= 8) {
    interpretation = 'TERMINAL_CONSOLIDATION';
    breakoutProbability = 70;
  } else if (total >= 5) {
    interpretation = 'MODERATE_COMPRESSION';
    breakoutProbability = 55;
  } else {
    interpretation = 'NO_COMPRESSION';
    breakoutProbability = 40;
  }

  return {
    total,
    bbWidthSignal,
    ribbonSignal,
    kcSqueezeSignal,
    crossoverSignal,
    interpretation,
    breakoutProbability
  };
}

// --- v4.0: VIX-ADJUSTED POSITION SIZING ---

function getVixSizeMultiplier(vix: number): number {
  if (vix < VIX_SIZE_MULTIPLIERS.calm.max) return VIX_SIZE_MULTIPLIERS.calm.multiplier;
  if (vix < VIX_SIZE_MULTIPLIERS.normal.max) return VIX_SIZE_MULTIPLIERS.normal.multiplier;
  if (vix < VIX_SIZE_MULTIPLIERS.elevated.max) return VIX_SIZE_MULTIPLIERS.elevated.multiplier;
  if (vix < VIX_SIZE_MULTIPLIERS.high.max) return VIX_SIZE_MULTIPLIERS.high.multiplier;
  return VIX_SIZE_MULTIPLIERS.extreme.multiplier;
}

// --- SCORING FUNCTIONS ---

function scoreSentiment(market: MarketData): StepScore {
  let points = 0;
  const details = [];

  // Sector performance - continuous scoring
  if (market.sectorPerformance > 2.0) {
    points += 6;
    details.push(`✅ Excellent Sector Strength (+${market.sectorPerformance.toFixed(1)}%)`);
  } else if (market.sectorPerformance > 1.0) {
    points += 4 + Math.round(market.sectorPerformance);
    details.push(`✅ Strong Sector Rel. Strength (+${market.sectorPerformance.toFixed(1)}%)`);
  } else if (market.sectorPerformance > 0) {
    points += 2 + Math.round(market.sectorPerformance);
    details.push(`- Sector Performing (+${market.sectorPerformance.toFixed(1)}%)`);
  } else {
    points += Math.max(0, 2 + Math.round(market.sectorPerformance));
    details.push(`⚠️ Sector Weakness (${market.sectorPerformance.toFixed(1)}%)`);
  }

  // VIX environment - More impactful scoring during high volatility
  const vix = market.vix ?? 18;
  if (vix < 15) {
    points += 5;
    details.push(`✅ Low VIX (${vix.toFixed(1)}) - Calm Markets`);
  } else if (vix < 20) {
    points += 4;
    details.push(`✅ VIX Normal (${vix.toFixed(1)})`);
  } else if (vix < 25) {
    points += 2;
    details.push(`⚠️ VIX Elevated (${vix.toFixed(1)}) - Increased Risk`);
  } else if (vix < 30) {
    points += 0;
    details.push(`⚠️ High VIX (${vix.toFixed(1)}) - Volatile Markets`);
  } else {
    points -= 2; // Penalty for extreme volatility
    details.push(`🚨 EXTREME VIX (${vix.toFixed(1)}) - High Uncertainty`);
  }

  // Price momentum based on change percent - uses real data that differs per ticker
  const changePercent = market.changePercent;
  if (changePercent > 2) {
    points += 4;
    details.push(`✅ Strong Momentum (+${changePercent.toFixed(2)}%)`);
  } else if (changePercent > 0.5) {
    points += 3;
    details.push(`✅ Positive Trend (+${changePercent.toFixed(2)}%)`);
  } else if (changePercent > -0.5) {
    points += 2;
    details.push(`- Neutral Day (${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%)`);
  } else if (changePercent > -2) {
    points += 1;
    details.push(`- Slight Weakness (${changePercent.toFixed(2)}%)`);
  } else {
    points += 0;
    details.push(`⚠️ Down Day (${changePercent.toFixed(2)}%)`);
  }

  return { points, maxPoints: 15, passed: points >= 8, details };
}

function scoreTechnicals(tech: TechnicalData): StepScore {
  let points = 0;
  const details = [];

  // EMA position - continuous scoring based on days above
  if (tech.priceAboveEma21) {
    const daysBonus = Math.min(3, Math.floor(tech.daysAboveEma21 / 5)); // 0-3 bonus points
    points += 3 + daysBonus;
    details.push(`✅ Price > 21 EMA (${tech.daysAboveEma21} days)`);
  } else {
    points += 1;
    details.push(`- Price Below 21 EMA`);
  }

  // RSI - continuous scoring with optimal range 45-55
  const rsiOptimalDistance = Math.abs(tech.rsi - 50);
  if (tech.rsi >= 40 && tech.rsi <= 60) {
    points += 6 - Math.round(rsiOptimalDistance / 5); // 4-6 points
    details.push(`✅ RSI Healthy (${tech.rsi.toFixed(0)})`);
  } else if (tech.rsi > 30 && tech.rsi < 70) {
    points += 3;
    details.push(`- RSI at ${tech.rsi.toFixed(0)}`);
  } else {
    points += 1;
    details.push(`⚠️ RSI Extreme (${tech.rsi.toFixed(0)})`);
  }

  // MACD crossover
  if (tech.macd.crossover === 'BULLISH') {
    points += 5;
    details.push(`✅ MACD Bullish Crossover`);
  } else if (tech.macd.crossover === 'BEARISH') {
    points += 2;
    details.push(`- MACD Bearish`);
  } else {
    points += 3;
    details.push(`- MACD Neutral`);
  }

  // v4.0: Enhanced squeeze detection with Coil Score
  const coilScore = calculateCoilScore(tech);

  if (coilScore.interpretation === 'TERMINAL_CONSOLIDATION') {
    points += 6;
    details.push(`🔥 COIL SCORE ${coilScore.total}/10 — Terminal consolidation, ${coilScore.breakoutProbability}% breakout probability`);
  } else if (coilScore.interpretation === 'MODERATE_COMPRESSION') {
    points += 4;
    details.push(`🔥 Coil Score ${coilScore.total}/10 — Moderate compression detected`);
  } else if (tech.squeeze) {
    // Fallback to legacy squeeze if coil score is low
    points += 3;
    details.push(`🔥 TTM Squeeze Detected`);
  } else if (tech.atrTrending) {
    points += 2;
    details.push(`- ATR Trending`);
  }

  // v4.0: Volume surge bonus
  if (tech.volumeRatio !== undefined && tech.volumeRatio >= 1.5) {
    points += 2;
    details.push(`✅ Volume Surge (${(tech.volumeRatio * 100).toFixed(0)}% of avg)`);
  }

  // Store coil score on tech object for use elsewhere
  tech.coilScore = coilScore.total;

  return { points, maxPoints: 20, passed: points >= 10, details };
}

function scoreLiquidity(opt: OptionsData): StepScore {
  let points = 0;
  const details = [];
  if (opt.liquidityGrade === 'A') { points += 15; details.push(`✅ Grade A Liquidity`); }
  else if (opt.liquidityGrade === 'B') { points += 10; details.push(`- Grade B Liquidity`); }
  return { points, maxPoints: 15, passed: points >= 10, details };
}

function scoreRisk(flags: RiskFlags): StepScore {
  let points = 25;
  const details = [];

  // v4.0: Enhanced earnings proximity — stricter thresholds
  if (flags.earningsInDays !== null && flags.earningsInDays <= EARNINGS_THRESHOLDS_V4.autoReject) {
    points -= 25; // AUTO NO_TRADE: ≤5 days is gambling
    details.push(`🚨 EARNINGS in ${flags.earningsInDays}d — AUTO NO_TRADE, binary event imminent`);
  } else if (flags.earningsInDays !== null && flags.earningsInDays <= EARNINGS_THRESHOLDS_V4.severe) {
    points -= 18; // Severe: 6-7 days, IV inflated, gap risk is real
    details.push(`🚨 EARNINGS in ${flags.earningsInDays}d — IV inflated, binary event risk`);
  } else if (flags.earningsInDays !== null && flags.earningsInDays <= EARNINGS_THRESHOLDS_V4.moderate) {
    points -= 12; // Moderate: 8-10 days
    details.push(`⚠️ Earnings Approaching (${flags.earningsInDays}d) — Consider hedging`);
  } else if (flags.earningsInDays !== null && flags.earningsInDays <= EARNINGS_THRESHOLDS_V4.minor) {
    points -= 8; // Minor: 11-14 days
    details.push(`⚠️ Earnings in ${flags.earningsInDays}d — Adjust sizing`);
  } else if (flags.earningsInDays !== null &&
             flags.earningsInDays >= EARNINGS_THRESHOLDS_V4.catalystWindowStart &&
             flags.earningsInDays <= EARNINGS_THRESHOLDS_V4.catalystWindowEnd) {
    // CATALYST WINDOW: 2-8 weeks out is a positive setup (v4.0: increased bonus)
    points += EARNINGS_THRESHOLDS_V4.catalystBonus;
    details.push(`✅ Catalyst Window: Earnings in ${flags.earningsInDays}d — ideal positioning window`);
  }

  // FOMC proximity
  if (flags.fomcInDays !== null && flags.fomcInDays <= 2) {
    points -= 12;
    details.push(`🚨 FOMC in ${flags.fomcInDays}d — Rate decision imminent`);
  } else if (flags.fomcInDays !== null && flags.fomcInDays < 5) {
    points -= 8;
    details.push(`⚠️ FOMC in ${flags.fomcInDays}d`);
  } else if (flags.fomcInDays !== null && flags.fomcInDays < 10) {
    points -= 4;
    details.push(`- FOMC in ${flags.fomcInDays}d — Monitor`);
  }

  // v4.0: Increased IV Rank penalty
  if (flags.ivRank > 80) {
    points -= 7;
    details.push(`⚠️ Extreme IV Rank (${flags.ivRank}) — High vega risk`);
  } else if (flags.ivRank > 70) {
    points -= 3;
    details.push(`- Elevated IV Rank (${flags.ivRank})`);
  }

  // v4.0: Sector catalyst handling
  if (flags.sectorCatalyst && flags.sectorCatalyst.type !== 'NONE') {
    const catalyst = flags.sectorCatalyst;
    if (catalyst.daysUntil !== null && catalyst.daysUntil <= 7) {
      points -= Math.abs(catalyst.penalty);
      details.push(`⚠️ Sector Catalyst: ${catalyst.type} in ${catalyst.daysUntil}d${catalyst.description ? ` — ${catalyst.description}` : ''}`);
    }
  }

  if (points < 0) points = 0;
  return { points, maxPoints: 25, passed: points >= 15, details };
}

// Score Step 5: Greeks & Delta/Theta Analysis
function scoreGreeks(opt: OptionsData, tech: TechnicalData): StepScore {
  let points = 0;
  const details = [];

  // Delta/Theta ratio analysis based on IV conditions - use CONTINUOUS scoring
  const ivRank = opt.ivRank;

  // Continuous IV scoring: optimal around 30-40, decreases as you move away
  if (ivRank < 20) {
    points += 6 + Math.round(ivRank / 5); // 6-9 points
    details.push(`✅ Low IV Rank (${ivRank}) - Favorable for Debit Spreads`);
  } else if (ivRank >= 20 && ivRank <= 40) {
    points += 10 + Math.round((40 - ivRank) / 5); // 10-14 points
    details.push(`✅ Optimal IV Rank (${ivRank}) - Balanced Environment`);
  } else if (ivRank > 40 && ivRank <= 60) {
    points += 8 + Math.round((60 - ivRank) / 5); // 8-11 points
    details.push(`- Elevated IV Rank (${ivRank}) - Consider Credit Strategies`);
  } else {
    points += Math.max(3, 8 - Math.round((ivRank - 60) / 10)); // 3-7 points
    details.push(`⚠️ High IV Rank (${ivRank}) - Theta Decay Favorable for Sellers`);
  }

  // RSI-based momentum confirmation - continuous scoring
  const rsiDistance = Math.abs(tech.rsi - 50);
  if (rsiDistance <= 15) {
    // RSI between 35-65 is good
    points += Math.round(5 - (rsiDistance / 5)); // 2-5 points
    details.push(`✅ RSI at ${tech.rsi.toFixed(0)} - Room to Move`);
  } else if (rsiDistance <= 25) {
    points += 2;
    details.push(`- RSI at ${tech.rsi.toFixed(0)} - Trending`);
  } else {
    points += 1;
    details.push(`⚠️ RSI at ${tech.rsi.toFixed(0)} - Extreme Level`);
  }

  // Liquidity affects Greeks reliability
  if (opt.liquidityGrade === 'A') {
    points += 3;
    details.push(`✅ High Liquidity - Reliable Greek Values`);
  } else if (opt.liquidityGrade === 'B') {
    points += 2;
    details.push(`- Moderate Liquidity`);
  }

  return { points, maxPoints: 20, passed: points >= 10, details };
}

// Score Step 6: Entry Criteria & Risk/Reward
function scoreEntry(market: MarketData, tech: TechnicalData, opt: OptionsData): StepScore {
  let points = 0;
  const details = [];

  // Price action vs support/resistance - use defaults if undefined
  const high52 = market.high52 ?? market.price * 1.2;
  const low52 = market.low52 ?? market.price * 0.8;
  const priceRange = high52 - low52;
  const pricePosition = priceRange > 0 ? (market.price - low52) / priceRange : 0.5;

  if (pricePosition >= 0.3 && pricePosition <= 0.7) {
    points += 8;
    details.push(`✅ Price in Middle Range (${(pricePosition * 100).toFixed(0)}% of 52-wk)`);
  } else if (pricePosition > 0.7) {
    points += 4;
    details.push(`- Price Near 52-wk High - Limited Upside Potential`);
  } else {
    points += 6;
    details.push(`- Price Near 52-wk Low - Higher Risk Entry`);
  }

  // Volume confirmation
  if (market.volume > market.avgVolume) {
    points += 5;
    details.push(`✅ Above Average Volume (${(market.volume / market.avgVolume * 100).toFixed(0)}%)`);
  } else {
    points += 2;
    details.push(`- Below Average Volume`);
  }

  // Risk/Reward based on ATR - use default if undefined
  const atr = tech.atr ?? 1.5;
  const atrPercent = (atr / market.price) * 100;
  if (atrPercent >= 1 && atrPercent <= 3) {
    points += 8;
    details.push(`✅ ATR ${atrPercent.toFixed(2)}% - Good Risk/Reward Setup`);
  } else if (atrPercent < 1) {
    points += 4;
    details.push(`- Low Volatility (ATR ${atrPercent.toFixed(2)}%) - Smaller Moves Expected`);
  } else {
    points += 3;
    details.push(`⚠️ High ATR ${atrPercent.toFixed(2)}% - Wider Stops Required`);
  }

  // IV strategy alignment
  if (opt.ivStrategy === 'BUY_PREMIUM' && tech.bias === 'BULLISH') {
    points += 4;
    details.push(`✅ Low IV + Bullish Bias = Ideal for Long Calls`);
  } else if (opt.ivStrategy === 'SELL_PREMIUM') {
    points += 3;
    details.push(`✅ High IV - Credit Spread Opportunity`);
  }

  return { points, maxPoints: 25, passed: points >= 12, details };
}

// Score Step 7: Trade Review & Final Check
function scoreReview(market: MarketData, tech: TechnicalData, opt: OptionsData, flags: RiskFlags): StepScore {
  let points = 0;
  const details = [];
  const warnings = [];

  // Check for conflicting signals
  let conflictCount = 0;

  // Technical vs IV alignment
  if (tech.bias === 'BULLISH' && opt.ivStrategy === 'SELL_PREMIUM') {
    conflictCount++;
    warnings.push(`Mixed: Bullish bias but high IV suggests caution`);
  }

  // RSI extremes
  if (tech.rsi > 70 || tech.rsi < 30) {
    conflictCount++;
    warnings.push(`RSI at extreme levels (${tech.rsi.toFixed(0)})`);
  }

  // Earnings proximity — weighted by severity
  if (flags.earningsInDays !== null && flags.earningsInDays <= 3) {
    conflictCount += 3; // Counts as 3 conflicts — this is extremely dangerous
    warnings.push(`Earnings in ${flags.earningsInDays} days — binary event imminent`);
  } else if (flags.earningsInDays !== null && flags.earningsInDays <= 7) {
    conflictCount += 2; // Counts as 2 conflicts
    warnings.push(`Earnings within ${flags.earningsInDays} days — IV inflated, gap risk`);
  } else if (flags.earningsInDays !== null && flags.earningsInDays <= 14) {
    conflictCount++;
    warnings.push(`Earnings in ${flags.earningsInDays} days`);
  }

  // Score based on warnings — steeper curve
  if (conflictCount === 0) {
    points = 10;
    details.push(`✅ No Major Conflicts Detected`);
  } else if (conflictCount === 1) {
    points = 6;
    details.push(`- Minor Concern: ${warnings[0]}`);
  } else if (conflictCount === 2) {
    points = 3;
    details.push(`⚠️ Multiple Warnings: ${warnings.join(', ')}`);
  } else {
    points = 1;
    details.push(`🚨 Critical Conflicts: ${warnings.join(', ')}`);
  }

  return { points, maxPoints: 10, passed: points >= 5, details };
}

// Score Step 8: Position Sizing
function scoreSizing(market: MarketData, opt: OptionsData, riskTolerance: number): StepScore {
  let points = 0;
  const details = [];

  // Liquidity for position sizing
  if (opt.liquidityGrade === 'A') {
    points += 6;
    details.push(`✅ Grade A Liquidity - Large Positions Possible`);
  } else if (opt.liquidityGrade === 'B') {
    points += 4;
    details.push(`✅ Grade B Liquidity - Standard Position Size`);
  } else {
    points += 2;
    details.push(`⚠️ Low Liquidity - Reduce Position Size`);
  }

  // Bid/ask spread impact
  if (opt.bidAskPercent <= 1.0) {
    points += 5;
    details.push(`✅ Tight Spreads (${opt.bidAskPercent.toFixed(1)}%) - Minimal Slippage`);
  } else if (opt.bidAskPercent <= 2.0) {
    points += 3;
    details.push(`- Moderate Spreads (${opt.bidAskPercent.toFixed(1)}%)`);
  } else {
    points += 1;
    details.push(`⚠️ Wide Spreads (${opt.bidAskPercent.toFixed(1)}%) - Consider Limit Orders`);
  }

  // Risk tolerance appropriateness
  if (riskTolerance === 5) {
    points += 4;
    details.push(`✅ Conservative Risk (5%) - Position Size Optimized`);
  } else {
    points += 3;
    details.push(`- Aggressive Risk (10%) - Larger Position Allowed`);
  }

  // v4.0: VIX-adjusted sizing feedback
  const vix = market.vix ?? 18;
  const vixMultiplier = getVixSizeMultiplier(vix);
  if (vixMultiplier < 1.0) {
    if (vixMultiplier <= 0.5) {
      details.push(`⚠️ VIX ${vix.toFixed(1)} — Size reduced to ${(vixMultiplier * 100).toFixed(0)}% (high volatility)`);
    } else {
      details.push(`- VIX ${vix.toFixed(1)} — Size adjusted to ${(vixMultiplier * 100).toFixed(0)}%`);
    }
  }

  return { points, maxPoints: 15, passed: points >= 8, details };
}

// Parse earnings date string to get days until
function parseEarningsDays(earningsDate: string | null): number | null {
  if (!earningsDate || earningsDate === 'N/A') return null;

  // Handle "X days (YYYY-MM-DD)" format
  const daysMatch = earningsDate.match(/^(\d+)\s*days/i);
  if (daysMatch) {
    return parseInt(daysMatch[1], 10);
  }

  // Handle direct date format
  const dateMatch = earningsDate.match(/\d{4}-\d{2}-\d{2}/);
  if (dateMatch) {
    const date = new Date(dateMatch[0]);
    if (!isNaN(date.getTime())) {
      return Math.ceil((date.getTime() - Date.now()) / (86400000));
    }
  }

  return null;
}

// Generate suitability reasons based on market conditions
function getSuitabilityReasons(tech: TechnicalData, opt: OptionsData, market: MarketData): string[] {
  const reasons: string[] = [];

  // RSI-based reasons
  if (tech.rsi > 50 && tech.rsi < 70) {
    reasons.push('RSI Shows Bullish Momentum');
  } else if (tech.rsi > 30 && tech.rsi < 50) {
    reasons.push('RSI Indicates Potential Reversal');
  }

  // IV-based reasons
  if (opt.ivRank < 30) {
    reasons.push('Low IV - Premium Buying Favorable');
  } else if (opt.ivRank > 60) {
    reasons.push('High IV - Credit Strategy Opportunity');
  }

  // Technical alignment
  if (tech.bias === 'BULLISH') {
    reasons.push('Technical Indicators Aligned Bullish');
  } else if (tech.bias === 'BEARISH') {
    reasons.push('Technical Indicators Aligned Bearish');
  }

  // Volume
  if (market.volume > market.avgVolume) {
    reasons.push('Strong Volume Confirmation');
  }

  // Liquidity
  if (opt.liquidityGrade === 'A') {
    reasons.push('Excellent Options Liquidity');
  }

  // If no specific reasons, add generic ones
  if (reasons.length === 0) {
    reasons.push('Defined Risk Strategy');
    reasons.push('Market Conditions Acceptable');
  }

  return reasons.slice(0, 4); // Return max 4 reasons
}

// Generate strategy warnings based on conditions
function getStrategyWarnings(tech: TechnicalData, opt: OptionsData, flags: RiskFlags): string[] {
  const warnings: string[] = [];

  // RSI extreme warnings
  if (tech.rsi > 70) {
    warnings.push('RSI overbought - potential reversal');
  } else if (tech.rsi < 30) {
    warnings.push('RSI oversold - potential bounce');
  }

  // IV warnings
  if (opt.ivRank > 80) {
    warnings.push('Extreme IV - expect vol crush after earnings');
  }

  // Earnings warning
  if (flags.earningsInDays !== null && flags.earningsInDays <= 14) {
    warnings.push(`Earnings in ${flags.earningsInDays} days - adjust position sizing`);
  }

  // FOMC warning
  if (flags.fomcInDays !== null && flags.fomcInDays <= 7) {
    warnings.push(`FOMC meeting in ${flags.fomcInDays} days`);
  }

  // Liquidity warning
  if (opt.liquidityGrade === 'C' || opt.liquidityGrade === 'D' || opt.liquidityGrade === 'F') {
    warnings.push('Low liquidity - use limit orders');
  }

  return warnings;
}

// --- MASTER ENGINE ---

export async function analyzeTickerFull(
  ticker: string,
  market: MarketData,
  tech: TechnicalData,
  opt: OptionsData,
  earningsDate: string | null,
  onProgress: (step: string, percent: number) => void,
  riskTolerance: 5 | 10,
  volMetrics?: VolatilityMetrics
): Promise<TickerAnalysis> {
  
  const fomc = checkFomcProximity();

  // Parse earnings date properly to avoid NaN
  const earningsDays = parseEarningsDays(earningsDate);

  // Build warning flags based on market conditions
  const warningFlags: string[] = [];
  const criticalFlags: string[] = [];

  // VIX-based warnings
  const vix = market.vix ?? 18;
  if (vix >= 30) {
    criticalFlags.push(`EXTREME VOLATILITY: VIX at ${vix.toFixed(1)} - Consider reducing position size or waiting`);
  } else if (vix >= 25) {
    warningFlags.push(`High VIX (${vix.toFixed(1)}): Markets are volatile - wider stops recommended`);
  } else if (vix >= 20) {
    warningFlags.push(`Elevated VIX (${vix.toFixed(1)}): Exercise caution with directional plays`);
  }

  // v4.0: Enhanced earnings warning thresholds
  if (earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.autoReject) {
    criticalFlags.push(`🚨 AUTO NO_TRADE: Earnings in ${earningsDays} days — Binary event imminent. v4.0 risk controls block all entries within ${EARNINGS_THRESHOLDS_V4.autoReject} days of earnings.`);
  } else if (earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.severe) {
    criticalFlags.push(`EARNINGS in ${earningsDays} days - Binary event risk. IV CRUSH WARNING: Implied volatility is inflated pre-earnings and will collapse 30-60% post-announcement regardless of direction. Avoid buying premium.`);
  } else if (earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.minor) {
    warningFlags.push(`Earnings in ${earningsDays} days - IV Crush risk: premiums are inflated pre-earnings. Selling premium may benefit from the IV drop, but buying premium carries crush risk.`);
  } else if (earningsDays !== null &&
             earningsDays >= EARNINGS_THRESHOLDS_V4.catalystWindowStart &&
             earningsDays <= EARNINGS_THRESHOLDS_V4.catalystWindowEnd) {
    // CATALYST WINDOW: earnings in 2-8 weeks is a POSITIVE signal per IgniteAI methodology
    warningFlags.push(`✅ CATALYST WINDOW: Earnings in ${earningsDays} days — ideal positioning window. Enter 30-45 DTE to capture pre-earnings run-up.`);
  }

  // FOMC warning with date
  const fomcDateStr = fomc.date ? ` (${fomc.date})` : '';
  if (fomc.days !== null && fomc.days <= 3) {
    criticalFlags.push(`FOMC meeting in ${fomc.days} days${fomcDateStr} - High volatility expected around rate decision`);
  } else if (fomc.days !== null && fomc.days <= 7) {
    criticalFlags.push(`FOMC in ${fomc.days} days${fomcDateStr} - Rate decision imminent. Expect elevated volatility.`);
  } else if (fomc.days !== null && fomc.days <= 14) {
    warningFlags.push(`FOMC in ${fomc.days} days${fomcDateStr} - Monitor for rate decision`);
  }

  // Liquidity warning
  if (opt.liquidityGrade === 'C' || opt.liquidityGrade === 'D' || opt.liquidityGrade === 'F') {
    warningFlags.push(`Low liquidity (Grade ${opt.liquidityGrade}) - Use limit orders`);
  }

  // IV Rank warning
  if (opt.ivRank > 80) {
    warningFlags.push(`Very High IV Rank (${opt.ivRank}) - Expect potential IV crush`);
  }

  // v4.0: Calculate VIX-adjusted position sizing multiplier
  const vixSizeMultiplier = getVixSizeMultiplier(vix);

  // v4.0: Calculate enhanced earnings penalty using new thresholds
  let earningsPenalty = 0;
  if (earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.autoReject) {
    earningsPenalty = 25;
  } else if (earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.severe) {
    earningsPenalty = 18;
  } else if (earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.moderate) {
    earningsPenalty = 12;
  } else if (earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.minor) {
    earningsPenalty = 8;
  }

  // Quantitative vol edge and regime warnings
  if (volMetrics && volMetrics.dataSource !== 'synthetic') {
    if (volMetrics.edgeStrength === 'STRONG' && volMetrics.edgeSignal === 'SELL_PREMIUM') {
      warningFlags.push(`VOL EDGE: IV (${volMetrics.currentIV.toFixed(0)}%) >> Realized (${volMetrics.ewmaForecast.toFixed(0)}%) — options overpriced, favor credit strategies`);
    } else if (volMetrics.edgeStrength === 'STRONG' && volMetrics.edgeSignal === 'BUY_PREMIUM') {
      warningFlags.push(`VOL EDGE: IV (${volMetrics.currentIV.toFixed(0)}%) << Realized (${volMetrics.ewmaForecast.toFixed(0)}%) — options cheap, favor debit strategies`);
    }
    if (volMetrics.regime.state === 'HIGH_VARIANCE' && volMetrics.regime.confidence > 0.6) {
      warningFlags.push(`REGIME: High variance (vol ratio ${volMetrics.regime.volRatio.toFixed(2)}x) — reduce position size, widen stops`);
    } else if (volMetrics.regime.state === 'COMPRESSING') {
      warningFlags.push(`REGIME: Volatility compressing — breakout imminent, consider long gamma`);
    }
    if (Math.abs(volMetrics.zScore20d) > 2.0) {
      warningFlags.push(`Z-SCORE: Price ${volMetrics.zScore20d > 0 ? 'extended' : 'oversold'} at ${volMetrics.zScore20d.toFixed(1)}σ — mean reversion risk`);
    }
  }

  const riskFlags: RiskFlags = {
    earningsInDays: earningsDays,
    fomcInDays: fomc.days,
    liquidityPass: opt.liquidityGrade === 'A' || opt.liquidityGrade === 'B',
    liquidityGrade: opt.liquidityGrade,
    ivRank: opt.ivRank,
    ivStrategy: opt.ivStrategy,
    warningFlags,
    criticalFlags,
    autoReject: (earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.autoReject) || (criticalFlags.length > 0 && vix >= 30),
    autoRejectReason: earningsDays !== null && earningsDays <= EARNINGS_THRESHOLDS_V4.autoReject
      ? `Earnings in ${earningsDays} days — AUTO NO_TRADE per v4.0 risk controls`
      : null,
    earningsPenalty,
    fomcPenalty: (fomc.days !== null && fomc.days <= 2) ? 12 : (fomc.days !== null && fomc.days <= 5) ? 8 : 0,
    totalPenalty: earningsPenalty + ((fomc.days !== null && fomc.days <= 2) ? 12 : (fomc.days !== null && fomc.days <= 5) ? 8 : 0),
    dayAfterFomc: false,
    fomcBias: 'NEUTRAL',
    vixLevel: vix,
    vixSizeMultiplier  // v4.0: VIX-adjusted sizing
  };

  onProgress('Running V3 Scoring...', 60);

  // Run all 8 scoring steps with real calculations
  const s1 = scoreSentiment(market);
  const s2 = scoreTechnicals(tech);
  const s3 = scoreLiquidity(opt);
  const s4 = scoreRisk(riskFlags);
  const s5 = scoreGreeks(opt, tech);
  const s6 = scoreEntry(market, tech, opt);
  const s7 = scoreReview(market, tech, opt, riskFlags);
  const s8 = scoreSizing(market, opt, riskTolerance);

  // === QUANTITATIVE VOL EDGE SCORING ADJUSTMENTS ===
  // Apply vol metrics to refine scoring when real data is available
  if (volMetrics && volMetrics.dataSource !== 'synthetic') {
    // Edge bonus/penalty on Step 3 (Options Surface)
    if (volMetrics.edgeStrength === 'STRONG') {
      s3.points = Math.min(s3.maxPoints, s3.points + 5);
      s3.details.push(`✅ STRONG vol edge: IV ${volMetrics.currentIV.toFixed(0)}% vs RV ${volMetrics.ewmaForecast.toFixed(0)}% (${volMetrics.edgeSignal})`);
    } else if (volMetrics.edgeStrength === 'MODERATE') {
      s3.points = Math.min(s3.maxPoints, s3.points + 3);
      s3.details.push(`✅ Moderate vol edge: IV ${volMetrics.currentIV.toFixed(0)}% vs RV ${volMetrics.ewmaForecast.toFixed(0)}%`);
    } else if (volMetrics.edgeStrength === 'NONE' && volMetrics.dataPoints > 30) {
      s3.details.push(`- No vol edge detected: IV ≈ RV (${volMetrics.currentIV.toFixed(0)}%)`);
    }

    // Regime bonus/penalty on Step 4 (Risk)
    if (volMetrics.regime.state === 'HIGH_VARIANCE' && volMetrics.regime.confidence > 0.6) {
      s4.points = Math.max(0, s4.points - 3);
      s4.details.push(`⚠️ High variance regime (${volMetrics.regime.volRatio.toFixed(2)}x) — reduce size`);
    } else if (volMetrics.regime.state === 'TRENDING' && volMetrics.regime.confidence > 0.5) {
      s4.points = Math.min(s4.maxPoints, s4.points + 2);
      s4.details.push(`✅ Trending regime — directional edge confirmed`);
    }

    // Z-score on Step 6 (Entry)
    if (Math.abs(volMetrics.zScore20d) > 2.0) {
      s6.points = Math.max(0, s6.points - 3);
      s6.details.push(`⚠️ Price ${volMetrics.zScore20d > 0 ? 'extended' : 'oversold'} (${volMetrics.zScore20d.toFixed(1)}σ) — mean reversion risk`);
    } else if (Math.abs(volMetrics.zScore20d) < 1.0) {
      s6.points = Math.min(s6.maxPoints, s6.points + 2);
      s6.details.push(`✅ Price near mean (${volMetrics.zScore20d.toFixed(1)}σ) — favorable entry zone`);
    }
  }

  // Calculate total using new max points: 15 + 20 + 15 + 25 + 20 + 25 + 10 + 15 = 145
  const totalMaxPoints = s1.maxPoints + s2.maxPoints + s3.maxPoints + s4.maxPoints +
                         s5.maxPoints + s6.maxPoints + s7.maxPoints + s8.maxPoints;
  const totalPoints = s1.points + s2.points + s3.points + s4.points +
                      s5.points + s6.points + s7.points + s8.points;
  let normalized = Math.round((totalPoints / totalMaxPoints) * 100);

  // --- DYNAMIC STRATEGY GENERATION (Using Strategy Engine) ---

  const livePrice = market.price;
  const snapshotPrice = market.prevClose || market.price;

  // Generate multiple strategies based on market direction and IV environment
  const strategies = generateStrategies(market, tech, opt, riskFlags, riskTolerance, volMetrics);

  // Get the best strategy (first one after sorting by suitability)
  const strategy = strategies[0] || {
    id: 'strat-default',
    name: 'Bull Call Spread',
    type: 'VERTICAL',
    direction: 'BULLISH' as const,
    suitabilityScore: 50,
    suitabilityReasons: ['Default strategy due to insufficient data'],
    riskProfile: 'DEFINED' as const,
    creditDebit: 'DEBIT' as const,
    legs: [],
    maxProfit: 0,
    maxLoss: 0,
    breakeven: livePrice,
    probabilityOfProfit: 0.5,
    riskRewardRatio: 1,
    greeks: {
      delta: 0, theta: 0, gamma: 0, vega: 0,
      deltaIdeal: 'POSITIVE', vegaIdeal: 'NEGATIVE',
      thetaIdeal: 'NEUTRAL', gammaIdeal: 'LOW', thetaDailyDollar: 0
    },
    warnings: ['Insufficient data for strategy calculation']
  };

  // Adjust legs for synthetic live pricing
  if (strategy.legs && strategy.legs.length > 0) {
    strategy.legs = strategy.legs.map((leg: OptionLeg) => ({
      ...leg,
      mid: (leg.bid + leg.ask) / 2,
      syntheticPrice: calculateSyntheticPrice(
        (leg.bid + leg.ask) / 2,
        snapshotPrice,
        livePrice,
        leg.delta,
        leg.gamma || 0
      )
    }));
  }

  const sizing: PositionSizing = {
    accountSize: 10000,
    riskTolerancePercent: riskTolerance,
    riskToleranceDollars: 10000 * (riskTolerance / 100),
    maxRiskDollars: 10000 * (riskTolerance / 100),
    vixAdjustment: 1,
    adjustedMaxRisk: 500,
    riskPerContract: strategy.maxLoss,
    maxContracts: 2,
    recommendedContracts: 1,
    capitalRequired: strategy.maxLoss,
    canAfford: true,
    sizeWarnings: []
  };

  // === BUILD TRADE PLAN (IgniteAI 5-Step Methodology) ===
  const isCreditSpread = strategy.creditDebit === 'CREDIT';
  const breakevenPrice = Array.isArray(strategy.breakeven) ? strategy.breakeven[0] : strategy.breakeven;
  const isBullish = strategy.direction === 'BULLISH';
  const isBearish = strategy.direction === 'BEARISH';

  // ATR-based buffer (fallback: 2% of price)
  const atrBuffer = tech.atr ?? livePrice * 0.02;

  // --- ENTRY GATE: 3 questions must all be YES ---
  const entryGate = {
    q1_moveScenario: true,  // Is there a 50%+ move or loss scenario?
    q2_stopDefined: true,   // Is the exact stop loss defined?
    q3_riskReward: strategy.maxProfit > 0 && strategy.maxLoss > 0
      ? (strategy.maxProfit / strategy.maxLoss) >= 1.0
      : false  // R:R must be at least 1:1
  };
  // Credit spreads inherently have <1:1 R:R by design but high win probability
  // They pass the gate if probability of profit > 60%
  if (isCreditSpread && strategy.probabilityOfProfit >= 0.6) {
    entryGate.q3_riskReward = true;
  }
  const entryGatePassed = entryGate.q1_moveScenario && entryGate.q2_stopDefined && entryGate.q3_riskReward;

  // --- POSITION SIZING (work backward from max loss) ---
  // Robinhood constraint: can't close half of 1 contract, so prefer 2+ for trim rule
  const accountSize = sizing.accountSize;
  const maxRiskDollars = accountSize * (riskTolerance / 100);
  const rawContracts = strategy.maxLoss > 0
    ? Math.floor(maxRiskDollars / strategy.maxLoss)
    : 1;
  // Prefer even numbers (2, 4, 6...) so 50% trim is clean; minimum 1
  let contractsFromRisk = Math.max(1, rawContracts >= 3 ? (rawContracts % 2 === 0 ? rawContracts : rawContracts - 1) : rawContracts);

  // --- KELLY CRITERION REFINEMENT ---
  // Re-compute vol metrics with actual strategy parameters for accurate Kelly
  let finalVolMetrics = volMetrics;
  if (volMetrics && strategy.maxProfit > 0 && strategy.maxLoss > 0) {
    // Recompute Kelly with actual strategy P/L
    const kellyP = strategy.probabilityOfProfit || 0.5;
    const kellyB = strategy.maxProfit / strategy.maxLoss;
    const kellyQ = 1 - kellyP;
    let kellyF = (kellyP * kellyB - kellyQ) / kellyB;
    if (kellyF > 0) {
      const halfKellyF = Math.min(kellyF * 0.5, 0.15); // Half-Kelly, cap 15%
      const kellyDollars = accountSize * halfKellyF;
      const kellyContracts = Math.max(1, Math.floor(kellyDollars / strategy.maxLoss));
      // Use the MORE CONSERVATIVE of risk-based and Kelly-based sizing
      const kellyAdjusted = Math.min(contractsFromRisk, kellyContracts);
      if (kellyAdjusted < contractsFromRisk) {
        contractsFromRisk = Math.max(1, kellyAdjusted);
        sizing.sizeWarnings.push(`Kelly sizing: ${kellyContracts} contracts (half-Kelly ${(halfKellyF * 100).toFixed(1)}% of account)`);
      }
      // Update volMetrics with final Kelly values
      if (finalVolMetrics) {
        finalVolMetrics = { ...finalVolMetrics, kellyFraction: Number(kellyF.toFixed(4)), halfKelly: Number(halfKellyF.toFixed(4)), kellyContracts };
      }
    }
  }

  const canTrimHalf = contractsFromRisk >= 2;

  // Update sizing with proper calculation
  sizing.maxRiskDollars = maxRiskDollars;
  sizing.riskPerContract = strategy.maxLoss;
  sizing.recommendedContracts = contractsFromRisk;
  sizing.maxContracts = Math.max(1, Math.floor((maxRiskDollars * 1.5) / Math.max(1, strategy.maxLoss)));
  sizing.capitalRequired = strategy.maxLoss * contractsFromRisk;
  sizing.canAfford = sizing.capitalRequired <= maxRiskDollars;

  let stopPrice: number;
  let stopCondition: string;
  let takeProfitPrice: number;
  let takeProfitPercent: number;
  let takeProfitAction: string;
  let planRationale: string;
  let planChecklist: string[];

  if (isCreditSpread) {
    // Credit spreads: manage around breakeven and short strike
    const shortStrike = Math.min(...strategy.legs.filter(l => l.action === 'SELL').map(l => l.strike));

    if (isBullish) {
      stopPrice = shortStrike - (strategy.maxProfit / 100);
      stopCondition = `Close below short strike ($${shortStrike}) breakeven zone — thesis invalidated`;
      takeProfitPrice = livePrice + atrBuffer;
    } else {
      const shortCallStrike = Math.max(...strategy.legs.filter(l => l.action === 'SELL').map(l => l.strike));
      stopPrice = shortCallStrike + (strategy.maxProfit / 100);
      stopCondition = `Close above short strike ($${shortCallStrike}) breakeven zone — thesis invalidated`;
      takeProfitPrice = livePrice - atrBuffer;
    }

    takeProfitPercent = 50;
    if (canTrimHalf) {
      const trimCount = Math.floor(contractsFromRisk / 2);
      takeProfitAction = `RULE: Close ${trimCount} of ${contractsFromRisk} contracts at 50% profit ($${Math.round(strategy.maxProfit * 0.5)}) — non-negotiable`;
      planChecklist = [
        `✅ TRIM: Close ${trimCount} of ${contractsFromRisk} at 50% profit ($${Math.round(strategy.maxProfit * 0.5)})`,
        `🎯 HOLD: Remaining ${contractsFromRisk - trimCount} — let theta work, trail stop to breakeven`,
      ];
    } else {
      takeProfitAction = `RULE: Close full position at 50% profit ($${Math.round(strategy.maxProfit * 0.5)}) — 1 contract, can't split`;
      planChecklist = [
        `✅ CLOSE: 1 contract — close entire position at 50% profit ($${Math.round(strategy.maxProfit * 0.5)})`,
      ];
    }
    planRationale = `Credit spread: collect $${(strategy.maxProfit / 100).toFixed(2)}/share. ${canTrimHalf ? '50% trim rule applies' : 'Close at 50% profit (1 contract)'}. Stop at breakeven breach.`;
    planChecklist = [
      ...planChecklist,
      `🛑 STOP: Exit all if loss reaches 1x credit ($${strategy.maxProfit})`,
      `📅 DTE: Close by 7 DTE if still open`,
      earningsDays !== null ? `⚠️ EARNINGS: Close 2 days before earnings (day ${earningsDays})` : '✅ No imminent earnings',
      `📊 SIZING: ${contractsFromRisk} contract(s), max loss $${strategy.maxLoss * contractsFromRisk}`
    ];
  } else {
    // Debit spreads / directional trades — the primary structure
    const atrMultiple = 1.5;
    if (isBullish) {
      stopPrice = livePrice - (atrBuffer * atrMultiple);
      stopCondition = `Close below $${stopPrice.toFixed(2)} (1.5x ATR support) — thesis invalidated`;
      takeProfitPrice = livePrice + (atrBuffer * 2);
    } else if (isBearish) {
      stopPrice = livePrice + (atrBuffer * atrMultiple);
      stopCondition = `Close above $${stopPrice.toFixed(2)} (1.5x ATR resistance) — thesis invalidated`;
      takeProfitPrice = livePrice - (atrBuffer * 2);
    } else {
      stopPrice = livePrice - (atrBuffer * 2);
      stopCondition = "Close outside expected range";
      takeProfitPrice = livePrice;
    }

    takeProfitPercent = 50;
    const returnOnRisk = strategy.maxLoss > 0 ? ((strategy.maxProfit / strategy.maxLoss) * 100).toFixed(0) : '∞';
    if (canTrimHalf) {
      const trimCount = Math.floor(contractsFromRisk / 2);
      takeProfitAction = `RULE: Close ${trimCount} of ${contractsFromRisk} at 50% of max profit. Trail remainder to ${returnOnRisk}%+ return.`;
      planChecklist = [
        `✅ TRIM: Close ${trimCount} of ${contractsFromRisk} when profit reaches 50% of max ($${Math.round(strategy.maxProfit * 0.5)})`,
        `🎯 TRAIL: Remaining ${contractsFromRisk - trimCount} — trail stop to breakeven, target full max profit`,
      ];
    } else {
      takeProfitAction = `RULE: 1 contract — close at 50% profit ($${Math.round(strategy.maxProfit * 0.5)}), can't split position`;
      planChecklist = [
        `✅ CLOSE: 1 contract — close entire position at 50% of max profit ($${Math.round(strategy.maxProfit * 0.5)})`,
      ];
    }
    planRationale = `Debit trade: risk $${strategy.maxLoss} for potential $${strategy.maxProfit > 100000 ? '∞' : strategy.maxProfit}. ${canTrimHalf ? 'Trim 50% at first target, trail rest.' : 'Close at 50% profit (1 contract).'}`;
    planChecklist = [
      ...planChecklist,
      `🛑 STOP: Written at entry — $${stopPrice.toFixed(2)} (${stopCondition.split('—')[0].trim()})`,
      earningsDays !== null ? `⚠️ EARNINGS: Close 2 days before earnings (day ${earningsDays})` : '✅ No imminent earnings',
      `📅 DTE CLOCK: 21-14 DTE → reassess thesis. 14-7 DTE → close if not profitable. 7-0 DTE → close 100%.`,
      `📊 SIZING: ${contractsFromRisk} contract(s), max loss $${strategy.maxLoss * contractsFromRisk} (${riskTolerance}% of $${accountSize})`
    ];
  }

  const tradePlan: TradePlan = {
    entryPrice: livePrice,
    stopLoss: {
      price: stopPrice,
      condition: stopCondition,
      lossAmount: strategy.maxLoss * 0.5,
      multiple: 1
    },
    takeProfit: {
      price: takeProfitPrice,
      percent: takeProfitPercent,
      action: takeProfitAction
    },
    contracts: sizing.recommendedContracts,
    maxContracts: sizing.maxContracts,
    capitalRequired: sizing.capitalRequired,
    maxRiskDollars: sizing.maxRiskDollars,
    riskPerContract: sizing.riskPerContract,
    rationale: planRationale,
    checklist: planChecklist
  };

  // --- POST-SCORING SAFEGUARDS ---
  // Hard cap for dangerous setups that should never be rated "TRADE"

  // 1. Credit spread into imminent earnings: cap at CAUTION
  if (strategy.creditDebit === 'CREDIT' && earningsDays !== null && earningsDays <= 7) {
    normalized = Math.min(normalized, 55);
    if (!warningFlags.includes('Credit spread into imminent earnings — framework recommends waiting until after earnings')) {
      warningFlags.push('Credit spread into imminent earnings — framework recommends waiting until after earnings');
    }
  }

  // 2. Inverted risk/reward (risking 3x+ what you can make): cap at CAUTION
  if (strategy.maxLoss > 0 && strategy.maxProfit > 0) {
    const rrRatio = strategy.maxProfit / strategy.maxLoss;
    if (rrRatio < 0.25) {
      // 4:1+ against — this is a structurally bad trade
      normalized = Math.min(normalized, 50);
      if (!warningFlags.includes('Risk/reward is heavily inverted — consider alternative structure')) {
        warningFlags.push('Risk/reward is heavily inverted — consider alternative structure');
      }
    } else if (rrRatio < 0.33) {
      // 3:1 against — poor
      normalized = Math.min(normalized, 60);
    }
  }

  // 3. Both: credit spread + earnings + bad R:R = hard NO_TRADE
  if (strategy.creditDebit === 'CREDIT' && earningsDays !== null && earningsDays <= 7 &&
      strategy.maxLoss > 0 && strategy.maxProfit / strategy.maxLoss < 0.33) {
    normalized = Math.min(normalized, 40); // Force NO_TRADE
  }

  // 4. Entry gate failure — if R:R fails the gate, cap at CAUTION
  if (!entryGatePassed) {
    normalized = Math.min(normalized, 55);
    if (!entryGate.q3_riskReward) {
      warningFlags.push('Entry gate FAILED: R:R below 1:1 (or credit spread POP < 60%)');
    }
  }

  // Add entry gate questions to the top of the trade plan checklist
  const entryGateChecklist = [
    `${entryGate.q1_moveScenario ? '✅' : '❌'} Can this move 50%+ in my favor? ${entryGate.q1_moveScenario ? 'YES' : 'NO'}`,
    `${entryGate.q2_stopDefined ? '✅' : '❌'} Is my exact stop defined? ${entryGate.q2_stopDefined ? 'YES' : 'NO'}`,
    `${entryGate.q3_riskReward ? '✅' : '❌'} Is R:R at least 1:1? ${entryGate.q3_riskReward ? 'YES' : 'NO — PASS'}`
  ];
  tradePlan.checklist = [
    `--- ENTRY GATE ${entryGatePassed ? '✅ PASSED' : '❌ FAILED — DO NOT ENTER'} ---`,
    ...entryGateChecklist,
    `--- TRADE MANAGEMENT ---`,
    ...(tradePlan.checklist || [])
  ];

  return {
    ticker,
    currentPrice: livePrice,
    analyzedAt: new Date().toISOString(),
    scores: {
      total: totalPoints,
      normalizedTotal: normalized,
      step1_sentiment: s1,
      step2_technicals: s2,
      step3_options: s3,
      step4_risk: s4,
      step5_greeks: s5,
      step6_entry: s6,
      step7_review: s7,
      step8_sizing: s8
    },
    positionSizing: sizing,
    riskFlags,
    marketData: market,
    technicals: tech,
    optionsData: opt,
    strategies: strategies,
    bestStrategy: strategy,
    tradePlan,
    volMetrics: finalVolMetrics,
    status: normalized >= 70 ? 'TRADE' : normalized >= 50 ? 'CAUTION' : 'NO_TRADE',
    statusReason: `Technical Score: ${normalized}/100`
  };
}
